import React, { lazy, Suspense, useEffect, useState } from "react";
import Navbar from "./components/layout/Navbar";
import Hero from "./components/layout/Hero";
import ContentHub from "./components/layout/ContentHub";
import Footer from "./components/layout/Footer";
import { ADMIN_SUBDOMAIN } from "./config/constants";
import { useWorkshopVisibility } from "./hooks/useWorkshopVisibility";

// Loader ligero mientras se descarga un chunk diferido
function AdminLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#fdf8f0" }}>
      <div className="text-sm" style={{ color: "#9a3412" }}>Cargando panel…</div>
    </div>
  );
}

function NextUIWrap({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// Carga diferida del panel admin
const AdminPanel = lazy(() =>
  import("./components/admin/AdminPanel").then((m) => ({ default: m.AdminPanel })),
);

function App() {
  const [activeCategory, setActiveCategory] = useState("all");

  // Detección de ruta del administrador (hostname, hash o path)
  const [isAdminView, setIsAdminView] = useState(() => {
    const hostname = window.location.hostname;
    const hash = window.location.hash.toLowerCase();
    const pathname = window.location.pathname.toLowerCase();
    return (
      hostname === ADMIN_SUBDOMAIN ||
      hash === "#admin" ||
      hash === "#/admin" ||
      pathname === "/admin" ||
      pathname === "/admin/"
    );
  });

  const { isVisible: isWorkshopVisible, isLoading: isWorkshopLoading } =
    useWorkshopVisibility(!isAdminView);

  useEffect(() => {
    const handleRouteCheck = () => {
      const hostname = window.location.hostname;
      const hash = window.location.hash.toLowerCase();
      const pathname = window.location.pathname.toLowerCase();
      setIsAdminView(
        hostname === ADMIN_SUBDOMAIN ||
        hash === "#admin" ||
        hash === "#/admin" ||
        pathname === "/admin" ||
        pathname === "/admin/"
      );
    };

    window.addEventListener("hashchange", handleRouteCheck);
    window.addEventListener("popstate", handleRouteCheck);

    return () => {
      window.removeEventListener("hashchange", handleRouteCheck);
      window.removeEventListener("popstate", handleRouteCheck);
    };
  }, []);

  // Intersection Observer for reveal animations (solo en vista pública)
  useEffect(() => {
    if (isAdminView) return;

    const intersectionObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add("visible"),
        ),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
    );

    const observe = () => {
      document.querySelectorAll(".reveal").forEach((el) => {
        intersectionObserver.observe(el);
      });
    };

    observe();

    const mutationObserver = new MutationObserver(observe);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isAdminView]);

  if (isAdminView) {
    return (
      <Suspense fallback={<AdminLoader />}>
        <NextUIWrap>
          <AdminPanel
            onExit={() => {
              window.location.hash = "";
              if (window.location.pathname === "/admin" || window.location.pathname === "/admin/") {
                window.location.pathname = "/";
              } else {
                setIsAdminView(false);
              }
            }}
          />
        </NextUIWrap>
      </Suspense>
    );
  }

  return (
    <NextUIWrap>
      <main className="min-h-screen font-body bg-white">
        <Navbar isWorkshopVisible={isWorkshopVisible} />
        <Hero />
        <ContentHub
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          isWorkshopVisible={isWorkshopVisible}
          isWorkshopLoading={isWorkshopLoading}
        />
        <Footer />
      </main>
    </NextUIWrap>
  );
}

export default App;
